import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import translations from './i18n';

// Supported languages and their URL prefixes (italian = root, no prefix)
export const LANGS = ['it', 'en', 'fr', 'de', 'es'];
export const DEFAULT_LANG = 'it';

// Localized slug for /integrazioni per language (URLs Google indexes)
const INTEGR_SLUG = { it: 'integrazioni', en: 'integrations', fr: 'integrations', de: 'integrationen', es: 'integraciones' };
const INTEGR_SLUG_LIST = Object.values(INTEGR_SLUG);

/** Rewrite `/integrazioni[/...][?...]` (or any localized form) using the slug for `lang`. */
export function localizeIntegrationsSlug(lang, path) {
  if (!path) return path;
  // Preserve query string / hash while translating the first segment
  const qIdx = path.search(/[?#]/);
  const suffix = qIdx >= 0 ? path.slice(qIdx) : '';
  let clean = qIdx >= 0 ? path.slice(0, qIdx) : path;
  const raw = clean.startsWith('/') ? clean.slice(1) : clean;
  const segs = raw.split('/');
  if (!segs.length) return path;
  const target = INTEGR_SLUG[lang] || 'integrazioni';
  if (INTEGR_SLUG_LIST.includes(segs[0])) {
    segs[0] = target;
    return '/' + segs.join('/') + suffix;
  }
  return path;
}

export const LANG_LABELS = {
  it: { name: 'Italiano',  flag: '🇮🇹', short: 'IT' },
  en: { name: 'English',   flag: '🇬🇧', short: 'EN' },
  fr: { name: 'Français',  flag: '🇫🇷', short: 'FR' },
  de: { name: 'Deutsch',   flag: '🇩🇪', short: 'DE' },
  es: { name: 'Español',   flag: '🇪🇸', short: 'ES' },
};

const LangCtx = createContext({
  lang: DEFAULT_LANG,
  t: translations[DEFAULT_LANG],
  setLang: () => {},
  withLang: (path) => path,
  stripLang: (path) => path,
});

/** Parse the language from a URL pathname.
 *  /en/blog → "en", /blog → "it" */
export function detectLang(pathname) {
  if (!pathname) return DEFAULT_LANG;
  const seg = pathname.split('/').filter(Boolean)[0];
  return LANGS.includes(seg) && seg !== DEFAULT_LANG ? seg : DEFAULT_LANG;
}

/** Strip the language prefix from a pathname.
 *  /en/blog → /blog ; /blog → /blog
 *  Also canonicalises any localized `/integrations|/integrationen|/integraciones` → `/integrazioni` (IT form). */
export function stripLangPrefix(pathname) {
  if (!pathname) return '/';
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && LANGS.includes(parts[0]) && parts[0] !== DEFAULT_LANG) {
    pathname = '/' + parts.slice(1).join('/');
  }
  // Canonicalise any localized integrations slug back to `/integrazioni`
  return localizeIntegrationsSlug(DEFAULT_LANG, pathname);
}

/** Add the language prefix to a path.
 *  ('en', '/blog') → '/en/blog' ; ('it', '/blog') → '/blog'
 *  Also translates `/integrazioni` slug per language. */
export function addLangPrefix(lang, path) {
  if (!path) path = '/';
  if (!path.startsWith('/')) path = '/' + path;
  // Localize integrations slug BEFORE adding the lang prefix
  path = localizeIntegrationsSlug(lang, path);
  if (lang === DEFAULT_LANG) return path;
  return `/${lang}${path === '/' ? '' : path}`;
}

export function LangProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLang(location.pathname);
  const t = translations[lang] || translations[DEFAULT_LANG];

  // Sync <html lang="..."> with current language
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    t,
    /** Switch language: navigates to the same page in the target language */
    setLang: (newLang) => {
      if (!LANGS.includes(newLang) || newLang === lang) return;
      const cleanPath = stripLangPrefix(location.pathname);
      const target = addLangPrefix(newLang, cleanPath) + (location.search || '');
      navigate(target);
      try { localStorage.setItem('bonsai_lang', newLang); } catch {}
    },
    /** Convert a path so it includes the current language prefix */
    withLang: (path) => addLangPrefix(lang, path),
    /** Remove language prefix (useful for matching) */
    stripLang: stripLangPrefix,
  }), [lang, t, location.pathname, location.search, navigate]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() { return useContext(LangCtx); }
